import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import authService from '../../services/auth'
import Card from '../../components/common/Card'
import Input from '../../components/common/Input'
import Button from '../../components/common/Button'
import Alert from '../../components/common/Alert'
import LoadingSpinner from '../../components/common/LoadingSpinner'

const InvitationAcceptPage = () => {
  const { token } = useParams()
  const navigate = useNavigate()
  const { acceptInvitationStep1, acceptInvitationStep2 } = useAuth()

  const [step, setStep] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [invitationData, setInvitationData] = useState(null)
  const [qrData, setQrData] = useState(null)
  const [backupCodes, setBackupCodes] = useState(null)

  const [formData, setFormData] = useState({
    username: '',
    first_name: '',
    last_name: '',
    password: '',
    password_confirm: '',
  })

  const [verificationCode, setVerificationCode] = useState('')

  // Validate invitation token on mount
  useEffect(() => {
    const validateToken = async () => {
      try {
        const data = await authService.validateInvitation(token)
        setInvitationData(data)
      } catch (err) {
        setError(err.response?.data?.detail || 'Invalid or expired invitation token')
      } finally {
        setLoading(false)
      }
    }

    validateToken()
  }, [token])

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const handleStep1Submit = async (e) => {
    e.preventDefault()
    setError('')

    // Validate passwords match
    if (formData.password !== formData.password_confirm) {
      setError('Passwords do not match')
      return
    }

    setLoading(true)

    try {
      const data = await acceptInvitationStep1({
        token,
        ...formData,
      })

      setQrData(data)
      setStep(2)
    } catch (err) {
      // Handle DRF validation errors
      if (err.response?.data) {
        const errorData = err.response.data

        // Check for field-specific errors
        if (typeof errorData === 'object' && !errorData.detail) {
          const errorMessages = Object.entries(errorData)
            .map(([field, messages]) => {
              const fieldName = field.charAt(0).toUpperCase() + field.slice(1).replace('_', ' ')
              const message = Array.isArray(messages) ? messages.join(', ') : messages
              return `${fieldName}: ${message}`
            })
            .join('\n')
          setError(errorMessages)
        } else {
          setError(errorData.detail || 'Failed to process invitation. Please try again.')
        }
      } else {
        setError('Failed to process invitation. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleStep2Submit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const data = await acceptInvitationStep2(verificationCode)

      // Store backup codes to show to user
      setBackupCodes(data.backup_codes)
      setStep(3)
    } catch (err) {
      setError(
        err.response?.data?.detail || 'Verification failed. Please check your code and try again.'
      )
    } finally {
      setLoading(false)
    }
  }

  const handleComplete = () => {
    navigate('/')
  }

  if (loading && !qrData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error && !invitationData) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <Card>
            <Alert type="error" message={error} />
            <div className="mt-4 text-center">
              <Button onClick={() => navigate('/login')}>Go to Login</Button>
            </div>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4 py-8">
      <div className="max-w-2xl w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Accept Invitation</h1>
          <p className="text-gray-600 mt-2">
            {step === 1 && 'Create your account'}
            {step === 2 && 'Set up two-factor authentication'}
            {step === 3 && 'Save your backup codes'}
          </p>
        </div>

        <Card>
          {error && <Alert type="error" message={error} onClose={() => setError('')} />}

          {/* Step 1: Account Details */}
          {step === 1 && (
            <form onSubmit={handleStep1Submit} className="space-y-4">
              <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Email:</strong> {invitationData?.email}
                </p>
              </div>

              <Input
                label="Username"
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                required
              />

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="First Name"
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                />

                <Input
                  label="Last Name"
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                />
              </div>

              <Input
                label="Password"
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
              />

              <Input
                label="Confirm Password"
                type="password"
                name="password_confirm"
                value={formData.password_confirm}
                onChange={handleChange}
                required
              />

              <Button type="submit" fullWidth disabled={loading}>
                {loading ? 'Processing...' : 'Continue'}
              </Button>
            </form>
          )}

          {/* Step 2: 2FA Setup */}
          {step === 2 && qrData && (
            <div className="space-y-6">
              <Alert
                type="info"
                message="Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)"
              />

              <div className="flex justify-center p-6 bg-white border rounded-lg">
                <img
                  src={`data:image/png;base64,${qrData.qr_code}`}
                  alt="QR Code for 2FA Setup"
                  className="w-64 h-64"
                />
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-700 mb-2">Manual Entry Code:</p>
                <code className="text-sm bg-white px-3 py-2 rounded border block text-center">
                  {qrData.secret}
                </code>
              </div>

              <form onSubmit={handleStep2Submit} className="space-y-4">
                <Input
                  label="Verification Code"
                  type="text"
                  name="code"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  placeholder="123456"
                  required
                  autoFocus
                />

                <Button type="submit" fullWidth disabled={loading}>
                  {loading ? 'Verifying...' : 'Verify & Complete Setup'}
                </Button>
              </form>
            </div>
          )}

          {/* Step 3: Backup Codes */}
          {step === 3 && backupCodes && (
            <div className="space-y-6">
              <Alert
                type="warning"
                message="Save these backup codes in a secure location. Each code can only be used once."
              />

              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-2 gap-3">
                  {backupCodes.map((code, index) => (
                    <code
                      key={index}
                      className="text-sm bg-white px-3 py-2 rounded border text-center font-mono"
                    >
                      {code}
                    </code>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <Button
                  onClick={() => navigator.clipboard.writeText(backupCodes.join('\n'))}
                  fullWidth
                  variant="outline"
                >
                  Copy to Clipboard
                </Button>
                <Button onClick={handleComplete} fullWidth>
                  I&apos;ve Saved My Backup Codes
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  )
}

export default InvitationAcceptPage
